import { ESortBy } from '@tacans/shared-marketplace-layer';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { RouteE } from 'src/constants/pages.constants';
import { useFetchDataForNftCards } from 'src/features/common/collection/hooks/use-fetch-data-for-nft-cards';
import { EmptyListPlaceholder } from 'src/features/common/shared/components/empty-list-placeholder';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { Grid } from 'src/ui/common/atoms/grid';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { LandingSection } from 'src/ui/common/layouts/landing-section';
import { NftCard } from '../../components/nft-card';
import { NftCardPreloader } from '../../components/nft-card-preloader';
import { useTrendingNfts } from '../../hooks/use-trending-nfts';

const CARDS_COUNT_DESKTOP = 8;
const CARDS_COUNT_TABLET = 6;

export const TrendingNftsContainer = ({ title }: { title?: string }) => {
    const { t } = useTranslation(['pages']);
    const { isUniverse, isUniverseOwner } = useUniverse();
    const router = useRouter();
    const { fetch, clear, trendingNfts, isLoading } = useTrendingNfts();
    const screenType = useScreenDetect();
    const fetchDataForNftCards = useFetchDataForNftCards();
    const { ref, inView } = useInView({
        triggerOnce: true,
    });
    const cardsCount = screenType.sm ? CARDS_COUNT_TABLET : CARDS_COUNT_DESKTOP;
    const nfts = useMemo(() => trendingNfts.slice(0, cardsCount), [trendingNfts, cardsCount]);
    const isEmptyState = !isLoading && !nfts.length;

    const gridConfig = useMemo(
        () => ({
            rowGap: 20,
            columnGap: screenType.xs || screenType.sm ? 10 : 20,
            xs: 2,
            sm: 3,
            md: 4,
            lg: 4,
            xl: 4,
        }),
        [screenType]
    );

    useEffect(() => {
        (isUniverse || inView) &&
            fetch({
                filter: {},
                sorting: {
                    sortBy: ESortBy.Date,
                    ascending: false,
                },
                pager: { page: 0, limit: 8 },
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUniverse, inView]);

    useEffect(() => {
        fetchDataForNftCards(trendingNfts);
    }, [fetchDataForNftCards, trendingNfts]);

    useEffect(
        () => clear,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleClickCreate = useCallback(() => {
        router.push(RouteE.COLLECTIBLE_CREATE);
    }, [router]);

    const EmptyPlaceholder = useMemo(() => {
        if (!isUniverse) {
            return null;
        }

        return (
            <EmptyListPlaceholder itemsGridConfig={gridConfig}>
                <Typography size="base" tag="div" variant="title" color="text">
                    {t('home.widgets.trendingNfts.emptyText')}
                </Typography>
                <Spacing mt="30px">
                    <Button variant="white" onClick={handleClickCreate}>
                        {t('home.widgets.trendingNfts.emptyButton')}
                    </Button>
                </Spacing>
            </EmptyListPlaceholder>
        );
    }, [isUniverse, gridConfig, handleClickCreate, t]);

    if (isUniverse && !isUniverseOwner && isEmptyState) {
        return null;
    }

    return (
        <div ref={ref}>
            <LandingSection seeMoreLink={RouteE.EXPLORE} title={title ?? t('home.widgets.trendingNfts.title')}>
                {!isEmptyState && (
                    <Grid {...gridConfig}>
                        {isLoading && [...Array(cardsCount).keys()].map((el) => <NftCardPreloader key={el} />)}
                        {!!nfts?.length && nfts!.map((nft) => <NftCard nft={nft} key={nft.id} />)}
                    </Grid>
                )}
                {isEmptyState && EmptyPlaceholder}
            </LandingSection>
        </div>
    );
};
